import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/*
  Fix summary vs your original:
  1. Heading no longer repeats the brand name. The thumbnail shows the
     brand ("Aurelix") + the division name (italic), exactly like the
     reference. The card body below shows a SEPARATE benefit headline
     ("We Drive Growth" etc.) instead of re-printing "Aurelix Growth".
  2. Thumbnail is now an organic "blob" shape (asymmetric border-radius)
     instead of a plain rectangle, matching the reference's soft,
     torn-edge look.
  3. Learn More pill now matches the reference: dark translucent pill
     with a colored label + arrow, not a solid color block.
  4. Grid uses auto-fit/minmax so it reflows smoothly at ANY viewport
     width (phone, small tablet, iPad, laptop, ultrawide) instead of
     jumping between three fixed breakpoints.
*/

const CAPABILITIES = [
  {
    id: 'growth',
    label: 'Growth',
    headline: 'We Drive Growth',
    description:
      'Web development, Shopify, CRO, paid media, SEO and growth systems designed to turn attention into action.',
    link: '/growth',
    accent: '#ec2b28',
    gradientFrom: '#6b0a0a',
    gradientTo: '#1a0505',
    glowStop: 'rgba(236,43,40,0.85)',
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    headline: 'We Uncover Insight',
    description:
      'Analytics, dashboards and business intelligence that turn fragmented data into clearer decisions.',
    link: '/intelligence',
    accent: '#3b82f6',
    gradientFrom: '#0a1b4a',
    gradientTo: '#050d1a',
    glowStop: 'rgba(59,130,246,0.85)',
  },
  {
    id: 'ai',
    label: 'AI',
    headline: 'We Build Agents',
    description:
      'AI agents, automations and custom solutions designed around your actual workflows — not generic templates.',
    link: '/aurelix-ai',
    accent: '#a855f7',
    gradientFrom: '#2e0a52',
    gradientTo: '#0f0318',
    glowStop: 'rgba(168,85,247,0.85)',
  },
];

function CapabilityCard({ cap, index }) {
  const navigate = useNavigate();

  return (
    <motion.div
      className="owled-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.52, delay: index * 0.08 }}
    >
      {/* ── Top gradient blob thumbnail (matches reference organic shape) ── */}
      <div
        className="owled-thumb"
        style={{
          background: `radial-gradient(ellipse 75% 60% at 50% 0%, ${cap.glowStop} 0%, ${cap.gradientFrom} 45%, ${cap.gradientTo} 100%)`,
        }}
      >
        <div className="owled-thumb-noise" />

        <div className="owled-thumb-brand">
          <span className="owled-thumb-aurelix">Aurelix</span>
          <span className="owled-thumb-label" style={{ color: cap.accent }}>
            {cap.label}
          </span>
        </div>
      </div>

      {/* ── Card body ── */}
      <div className="owled-body">
        <h3 className="owled-title">{cap.headline}</h3>
        <p className="owled-desc">{cap.description}</p>

        <div className="owled-footer">
          <button
            className="owled-btn"
            style={{ color: cap.accent, borderColor: `${cap.accent}55` }}
            onClick={() => navigate(cap.link)}
          >
            <span>Learn More</span>
            <ArrowUpRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function ConnectedSystem() {
  const navigate = useNavigate();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;800&family=Playfair+Display:ital,wght@1,700&display=swap');

        .owled-section {
          background: #000000;
          color: #ffffff;
          padding: clamp(60px, 8vh, 100px) clamp(16px, 4vw, 56px);
          font-family: 'Bricolage Grotesque', sans-serif;
          position: relative;
          overflow: hidden;
        }

        .owled-hero {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          margin-bottom: clamp(40px, 7vh, 80px);
        }

        .owled-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #ec2b28;
          padding: 5px 14px;
          border-radius: 999px;
          margin-bottom: 16px;
        }

        .owled-eyebrow-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #ec2b28;
          box-shadow: 0 0 8px #ec2b28;
          animation: owled-pulse 2s infinite ease-in-out;
          flex-shrink: 0;
        }

        @keyframes owled-pulse {
          0%,100% { opacity:1; transform:scale(1); }
          50% { opacity:0.35; transform:scale(0.72); }
        }

        .owled-hero-title {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(2.4rem, 5.5vw, 4.5rem);
          font-weight: 800;
          letter-spacing: -0.035em;
          line-height: 1.08;
          margin: 0 0 18px;
          color: #fff;
          text-align: center;
          text-transform: none;
        }

        .owled-hero-title-grad {
          background: linear-gradient(120deg, #fff 20%, rgba(236,43,40,0.9) 70%, rgba(168,85,247,0.8) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .owled-hero-desc {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(0.92rem, 1.25vw, 1.08rem);
          font-weight: 400;
          color: rgba(255, 255, 255, 0.65);
          line-height: 1.7;
          max-width: 580px;
          margin: 0 auto;
          text-align: center;
        }

        /* ── Grid: 3 equal columns, clean ── */
        .owled-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
          max-width: 1100px;
          margin: 0 auto;
        }

        @media (max-width: 900px) {
          .owled-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 580px) {
          .owled-grid {
            grid-template-columns: 1fr;
          }
        }

        .owled-card {
          background: #111111;
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border: 1px solid rgba(255,255,255,0.07);
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }

        .owled-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 24px 54px rgba(0,0,0,0.7);
          border-color: rgba(255,255,255,0.14);
        }

        /* Organic blob thumbnail — asymmetric radius instead of a plain rectangle */
        .owled-thumb {
          position: relative;
          height: clamp(150px, 20vw, 210px);
          margin: 10px 10px 0;
          border-radius: 26px 26px 46% 54% / 26px 26px 34% 40%;
          overflow: hidden;
          flex-shrink: 0;
        }

        .owled-thumb-noise {
          position: absolute;
          inset: 0;
          opacity: 0.06;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          pointer-events: none;
        }

        .owled-thumb-brand {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          z-index: 2;
        }

        .owled-thumb-aurelix {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(1.7rem, 3.4vw, 2.6rem);
          font-weight: 800;
          letter-spacing: -0.04em;
          color: #ffffff;
          text-shadow: 0 2px 24px rgba(0,0,0,0.6);
          line-height: 1;
        }

        .owled-thumb-label {
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-size: clamp(0.9rem, 1.7vw, 1.3rem);
          font-weight: 700;
          letter-spacing: 0.02em;
          text-shadow: 0 2px 16px rgba(0,0,0,0.4);
          line-height: 1;
          margin-top: 2px;
        }

        .owled-body {
          padding: clamp(16px, 2vw, 22px) clamp(18px, 2.2vw, 24px) clamp(18px, 2.2vw, 24px);
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        /* Body headline — distinct copy from the thumbnail, no repetition */
        .owled-title {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(1.1rem, 1.5vw, 1.3rem);
          font-weight: 700;
          line-height: 1.25;
          color: #ffffff;
          margin: 0 0 10px;
          letter-spacing: -0.02em;
        }

        .owled-desc {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 0.85rem;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.5);
          line-height: 1.65;
          margin: 0;
          flex: 1;
        }

        .owled-footer {
          margin-top: 18px;
          display: flex;
          justify-content: flex-end;
        }

        .owled-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 9px 18px;
          border-radius: 50px;
          background: rgba(255,255,255,0.04);
          border: 1px solid;
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 0.76rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          cursor: pointer;
          transition: background 0.22s ease, transform 0.22s ease;
        }

        .owled-btn:hover {
          background: rgba(255,255,255,0.08);
          transform: scale(1.03);
        }

        .owled-cta-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
          margin-top: clamp(40px, 6vh, 72px);
        }

        .owled-cta-line {
          height: 1px;
          flex: 1;
          max-width: 180px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1));
        }
        .owled-cta-line.r {
          background: linear-gradient(90deg, rgba(255,255,255,0.1), transparent);
        }

        .owled-cta-main {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 14px 36px;
          background: linear-gradient(135deg, #ec2b28 0%, #b31b18 100%);
          color: #fff;
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 0.78rem;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          border: none;
          border-radius: 50px;
          cursor: pointer;
          box-shadow: 0 8px 28px rgba(236,43,40,0.4);
          transition: all 0.28s ease;
        }

        .owled-cta-main:hover {
          transform: translateY(-3px);
          box-shadow: 0 14px 40px rgba(236,43,40,0.55);
        }

        .owled-cta-arrow { transition: transform 0.22s ease; }
        .owled-cta-main:hover .owled-cta-arrow { transform: translateX(5px); }

        @media (max-width: 640px) {
          .owled-section { padding-left: 16px; padding-right: 16px; }
          .owled-hero-title { font-size: 2rem; }
          .owled-cta-main { padding: 13px 26px; font-size: 0.7rem; }
        }
      `}</style>

      <section className="owled-section" id="capabilities">
        <div className="owled-hero">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="owled-eyebrow">
              <span className="owled-eyebrow-dot" />
              <span>Connected Ecosystem</span>
            </div>
            <h2 className="owled-hero-title">
              Three capabilities.<br />
              <span className="owled-hero-title-grad">One connected system.</span>
            </h2>
            <p className="owled-hero-desc">
              Most agencies{' '}
              <span className="scroll-hl" style={{ '--hl-d': '0s' }}>operate in silos</span>.
              {' '}Aurelix brings Growth, Intelligence and AI together —
              so{' '}
              <span className="scroll-hl" style={{ '--hl-d': '0.22s' }}>every decision compounds</span>
              {' '}and every channel reinforces the next.
            </p>
          </motion.div>
        </div>

        <div className="owled-grid">
          {CAPABILITIES.map((cap, index) => (
            <CapabilityCard key={cap.id} cap={cap} index={index} />
          ))}
        </div>

        <motion.div
          className="owled-cta-row"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="owled-cta-line" />
          <button className="owled-cta-main" onClick={() => navigate('/services')}>
            <span>Explore All Services</span>
            <ArrowUpRight size={16} className="owled-cta-arrow" />
          </button>
          <div className="owled-cta-line r" />
        </motion.div>
      </section>
    </>
  );
}